/************************************************************************************************
 * Copyright TRUSST AI PTY LTD. All Rights Reserved.                                            *
 *                                                                                              *
 * Licensed under the TRUSST SOFTWARE LICENSE (the "License"). You may not use this file except *
 * in compliance with the "LICENSE" file accompanying this file. This file is distributed       *
 * on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, express or implied.       *
 *                                                                                              *
 * See the "License" file for the specific language governing permissions and limitations       *
 * under the License and limitations under the License.                                         *
 ***********************************************************************************************/

import {useCallback, useState} from 'react';
import {useAgentsAPI} from './useAgentsAPI';
import type {Agent as AgentRemote} from '../../../components/TrusstedAgentLegacy/Types/types';
import {APIParams, METHODS} from '../../../hooks/abstractions/useTrusstedAgent';
import type {Agent as AgentLocal} from '../../../types/agent';
import {AGENTS_API_EVENT_NAMES} from '../../../types/agents-api';
import {mapAgentRemoteToLocal} from '../utils';

export const useGetAgents = () => {
  const agentsAPI = useAgentsAPI();
  const [agents, setAgents] = useState<AgentLocal[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const queryParams = {
        page_size: 100,
        page: 0,
      };
      const result = await agentsAPI({
        eventName: AGENTS_API_EVENT_NAMES.ListAgents,
        methodName: METHODS.GET,
        params: {},
        queryParams,
      } as APIParams);

      const remappedAgents = result.agents.map((remoteAgent: AgentRemote) => mapAgentRemoteToLocal(remoteAgent));
      setAgents(remappedAgents);
    } catch (err) {
      setError('Error fetching agents');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  }, [agentsAPI]);

  return {
    agents,
    isLoading,
    error,
    fetch,
  };
};
